// «QUÉ SE HACE AQUÍ» · la estación de una terminal · DISEÑO-F5 §7.1 (TP-10)
//
// Tres pasos apilados con progreso —operaciones, productos, botones
// rápidos— y un cuarto (destinos) solo si «Pasar a otro lugar» está
// marcado. Todo sobre `GET/PUT /terminales/{id}/catalogo` de F3, sin rutas
// nuevas. Un solo «Guardar»: la terminal lo recibe en su siguiente latido.
import { useEffect, useMemo, useState } from 'react';
import { api, ErrorApi } from '../api';
import Ayuda from '../ui/Ayuda';
import Motivo from '../experiencia/Motivo';
import { Cargando } from '../ui/EstadoDePantalla';
import { leerErrorDelContrato } from './errorDelContrato';
import { OPERACIONES, SKU_LIMPIO, cuerpoDeGuardado } from './estacionDeTerminalUtil';

function Paso({ n, titulo, children }) {
  return (
    <section className="space-y-2 rounded-xl border border-gray-200 bg-white p-4" data-paso={n}>
      <h2 className="font-semibold text-gray-900">Paso {n} · {titulo}</h2>
      {children}
    </section>
  );
}

export default function EstacionDeTerminal({ t, onCerrar, onHecho }) {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [idUbicacion, setIdUbicacion] = useState(null);
  const [operaciones, setOperaciones] = useState(['CONSUMO']);
  const [todos, setTodos] = useState(true);
  const [elegidos, setElegidos] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [encontrados, setEncontrados] = useState([]);
  const [botones, setBotones] = useState([1, 2, 3, 4, 5, 6].map((n) => ({ n, modo: 'AUTO', sku: '', cantidad: '' })));
  const [destinos, setDestinos] = useState([]);

  useEffect(() => {
    let vivo = true;
    api.get(`/terminales/${t.id}/catalogo`)
      .then((d) => {
        if (!vivo) return;
        setDatos(d);
        setIdUbicacion(d.ubicacion?.id || null);
        setOperaciones(d.operaciones?.length ? d.operaciones : ['CONSUMO']);
        setTodos(d.todos_de_la_ubicacion !== false);
        setElegidos((d.productos || []).map((p) => p.sku));
        setBotones([1, 2, 3, 4, 5, 6].map((n) => {
          const b = (d.botones || []).find((x) => x.n === n);
          return b && b.fijado_por === 'ADMIN'
            ? { n, modo: 'FIJO', sku: b.sku || '', cantidad: b.cantidad ?? '' }
            : { n, modo: 'AUTO', sku: '', cantidad: '' };
        }));
        setDestinos((d.destinos || []).map((x) => x.id_ubicacion));
      })
      .catch((e) => vivo && setError(textoDeError(e)));
    return () => { vivo = false; };
  }, [t.id]);

  useEffect(() => {
    if (!busqueda.trim() || todos) { setEncontrados([]); return undefined; }
    const h = setTimeout(() => {
      api.get(`/terminales/${t.id}/catalogo?buscar=${encodeURIComponent(busqueda.trim())}`)
        .then((d) => setEncontrados(d.encontrados || []))
        .catch(() => setEncontrados([]));
    }, 300);
    return () => clearTimeout(h);
  }, [busqueda, todos, t.id]);

  const pasos = operaciones.includes('TRASPASO') ? 4 : 3;
  const sinOperaciones = operaciones.length === 0;
  const sinProductos = !todos && elegidos.length === 0;
  const puedeGuardar = !sinOperaciones && !sinProductos && !guardando;
  const lugares = useMemo(() => {
    const l = [...(datos?.lugares || [])];
    if (datos?.ubicacion && !l.some((x) => x.id === datos.ubicacion.id)) l.unshift(datos.ubicacion);
    return l;
  }, [datos]);

  function alternar(op) {
    setOperaciones((prev) => (prev.includes(op) ? prev.filter((x) => x !== op) : [...prev, op]));
  }
  function alternarSku(sku) {
    const s = SKU_LIMPIO(sku);
    setElegidos((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  }
  function cambiarBoton(n, cambios) {
    setBotones((prev) => prev.map((b) => (b.n === n ? { ...b, ...cambios } : b)));
  }

  async function guardar() {
    setGuardando(true);
    setError(null);
    try {
      const d = await api.put(`/terminales/${t.id}/catalogo`,
        cuerpoDeGuardado({ idUbicacion, operaciones, todos, elegidos, botones, destinos }));
      onHecho?.(d?.mensaje || 'Se guardó. La terminal lo recibe en su siguiente latido (≤ 1 min).');
    } catch (e) {
      setError(textoDeError(e));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="space-y-4 max-w-3xl">
      <header className="space-y-1">
        <button type="button" onClick={onCerrar} className="min-h-11 text-sm font-semibold text-gray-700">← Volver a Terminales</button>
        <h1 className="text-xl font-bold text-gray-900">Qué se hace en {t.nombre}</h1>
        <p className="text-sm text-gray-600">
          Elige qué operaciones, qué productos y qué botones rápidos tiene esta terminal.
          <Ayuda id="terminal.estacion.pantalla" />
        </p>
      </header>
      {error && <div role="alert" className="text-sm text-red-700 bg-red-50 rounded-lg p-3">{error}</div>}
      {!datos && !error && <Cargando />}
      {datos && (
        <>
          <p className="text-sm text-gray-600" aria-live="polite">{pasos} pasos · uno solo «Guardar» al final.</p>
          <label className="block text-sm">
            Estación (repisa) de esta terminal
            <select
              className="entrada mt-1 w-full"
              value={idUbicacion || ''}
              onChange={(e) => setIdUbicacion(e.target.value ? Number(e.target.value) : null)}
            >
              {lugares.map((u) => <option key={u.id} value={u.id}>{u.codigo ? `${u.codigo} · ` : ''}{u.nombre}</option>)}
            </select>
            <span className="block text-xs text-gray-500 mt-1">La terminal tomará el catálogo de la ubicación nueva.</span>
          </label>

          <Paso n={1} titulo="Operaciones">
            {OPERACIONES.map(([clave, rotulo, explicacion]) => (
              <label key={clave} className="flex items-start gap-2 min-h-11">
                <input type="checkbox" className="mt-1" checked={operaciones.includes(clave)} onChange={() => alternar(clave)} />
                <span><strong>{rotulo}</strong> <span className="text-gray-600">— {explicacion}</span></span>
              </label>
            ))}
            <Motivo id={`motivo-ops-${t.id}`} cuando={sinOperaciones}>Marca al menos una cosa que se hace en esta estación.</Motivo>
          </Paso>

          <Paso n={2} titulo="Productos">
            <label className="flex items-center gap-2 min-h-11">
              <input type="checkbox" checked={todos} onChange={(e) => setTodos(e.target.checked)} />
              <span>Todos los de la repisa</span>
              <Ayuda id="terminal.estacion.todos" />
            </label>
            {todos && <p className="text-sm text-gray-600">Esta estación usa todos los productos de su repisa.</p>}
            {!todos && (
              <div className="space-y-2">
                <input
                  className="entrada w-full"
                  placeholder="Busca por SKU o nombre"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  onBlur={(e) => setBusqueda(SKU_LIMPIO(e.target.value) === e.target.value.trim().toUpperCase() ? e.target.value.trim() : e.target.value)}
                />
                {encontrados.map((p) => (
                  <label key={p.sku} className="flex items-center gap-2 min-h-11">
                    <input type="checkbox" checked={elegidos.includes(SKU_LIMPIO(p.sku))} onChange={() => alternarSku(p.sku)} />
                    <span><strong>{SKU_LIMPIO(p.sku)}</strong> · {p.nombre}</span>
                  </label>
                ))}
                {elegidos.length > 0 && (
                  <p className="text-sm text-gray-700">Elegidos: {elegidos.join(', ')}</p>
                )}
                <Motivo id={`motivo-prod-${t.id}`} cuando={sinProductos}>Elige al menos un producto, o marca «Todos los de la repisa».</Motivo>
              </div>
            )}
          </Paso>

          <Paso n={3} titulo="Botones rápidos">
            <p className="text-sm text-gray-600">
              Seis botones en la terminal, en dos filas de tres.
              <Ayuda id="terminal.estacion.botones" />
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-terminal="botones">
              {botones.map((b) => (
                <div key={b.n} className="rounded-lg border border-gray-200 p-2 space-y-1 min-h-12">
                  <div className="text-xs font-semibold text-gray-600">Botón {b.n}</div>
                  <select className="entrada w-full" value={b.modo} onChange={(e) => cambiarBoton(b.n, { modo: e.target.value })}>
                    <option value="AUTO">Automático (por uso)</option>
                    <option value="FIJO">Fijo: producto + cantidad</option>
                  </select>
                  {b.modo === 'FIJO' && (
                    <>
                      <input className="entrada w-full" placeholder="SKU" value={b.sku}
                             onChange={(e) => cambiarBoton(b.n, { sku: e.target.value })}
                             onBlur={(e) => cambiarBoton(b.n, { sku: SKU_LIMPIO(e.target.value) })} />
                      <input className="entrada w-full" placeholder="Cantidad" inputMode="decimal" value={b.cantidad}
                             onChange={(e) => cambiarBoton(b.n, { cantidad: e.target.value })} />
                    </>
                  )}
                </div>
              ))}
            </div>
          </Paso>

          {operaciones.includes('TRASPASO') && (
            <Paso n={4} titulo="Destinos">
              <p className="text-sm text-gray-600">A qué lugares se puede pasar desde aquí (hasta 10).</p>
              {(datos.lugares || []).map((u) => (
                <label key={u.id} className="flex items-center gap-2 min-h-11">
                  <input
                    type="checkbox"
                    checked={destinos.includes(u.id)}
                    disabled={!destinos.includes(u.id) && destinos.length >= 10}
                    onChange={() => setDestinos((prev) => (prev.includes(u.id) ? prev.filter((x) => x !== u.id) : [...prev, u.id]))}
                  />
                  <span>{u.codigo ? `${u.codigo} · ` : ''}{u.nombre}</span>
                </label>
              ))}
            </Paso>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={guardar}
              disabled={!puedeGuardar}
              aria-describedby={!puedeGuardar && !guardando ? `motivo-guardar-${t.id}` : undefined}
              className="inline-flex min-h-11 items-center bg-[var(--color-marca)] text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50"
            >
              {guardando ? 'Guardando…' : 'Guardar'}
            </button>
            <button type="button" onClick={onCerrar} className="min-h-11 rounded-lg border border-gray-300 bg-white px-3 text-sm">Cancelar</button>
          </div>
          <Motivo id={`motivo-guardar-${t.id}`} cuando={!puedeGuardar && !guardando}>
            {sinOperaciones ? 'Falta marcar qué se hace aquí (paso 1).' : 'Falta elegir productos o marcar «Todos los de la repisa» (paso 2).'}
          </Motivo>
        </>
      )}
    </div>
  );
}

function textoDeError(e) {
  if (e instanceof ErrorApi || e?.detail || e?.detalle) {
    const { mensaje, queHacer } = leerErrorDelContrato(e);
    return `${mensaje} ${queHacer}`.trim();
  }
  return 'No se pudo leer la estación. Vuelve a intentarlo.';
}
